import {
  ApolloProvider,
  ApolloClient,
  createHttpLink,
  Observable,
  ApolloLink
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import * as Sentry from "@sentry/react";
import { SentryLink } from "apollo-link-sentry";
import { GraphQLError } from "graphql";
import { ReactNode, useCallback, useEffect, useState } from "react";

import cache from "src/graphql/cache";
import getTenant from "src/util/getTenant";

import getAdminApiBaseUrl from "../util/getAdminApiBaseUrl";
import { useAuth0 } from "./AuthProvider";
import { useCaptcha } from "./CaptchaProvider";
import ErrorCard from "./ErrorCard";
import LoadingIndicator from "./LoadingIndicator";

const sentryErrorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    if (Array.isArray(graphQLErrors)) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        console.log(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
        );

        Sentry.captureMessage(message, {
          extra: { locations, path }
        });
      });
    } else {
      const { message, locations, path } =
        graphQLErrors as unknown as GraphQLError;
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      );

      Sentry.captureMessage(message, {
        extra: {
          locations,
          path
        }
      });
    }
  }
  if (networkError) {
    Sentry.captureException(networkError);
  }
});

const sentryBreadcrumbLink = new SentryLink();

export default function Auth0ApolloProvider({
  children
}: {
  children: ReactNode;
}): JSX.Element {
  const auth0 = useAuth0();
  const captcha = useCaptcha();

  const [httpLink, setHttpLink] = useState<ApolloLink | null>(null);
  const [initError, setInitError] = useState<any>(null);
  const initHttpLink = useCallback(async () => {
    try {
      setHttpLink(
        createHttpLink({
          uri: `${await getAdminApiBaseUrl()}/admin/graphql`
        })
      );
    } catch (e) {
      setInitError(e);
    }
  }, [setHttpLink]);
  useEffect(() => {
    if (!httpLink) {
      void initHttpLink();
    }
  }, [httpLink, initHttpLink]);

  const tenantLink = setContext(async (_, { headers }) => {
    return {
      headers: {
        ...headers,
        "STW-Dialer-Tenant": getTenant()
      }
    };
  });

  const authLink = setContext(async (_, { headers }) => {
    const token = await auth0.getAccessTokenSilently();
    if (window) {
      (window as any)["AUTH0_TOKEN"] = token;
    }
    return {
      headers: {
        ...headers,
        Authorization: token ? `Bearer ${token}` : "",
        ...captcha.getLatestTokenHeaders()
      }
    };
  });

  let captchaInProgress = false;
  const wafChallengeLink = onError(({ networkError, operation, forward }) => {
    if (
      networkError &&
      "response" in networkError &&
      networkError.response.status === 405
    ) {
      if (captchaInProgress) {
        console.log("WAF challenge already in progress; ignoring");
        return;
      }
      captchaInProgress = true;
      console.log("WAF challenge detected; showing captcha");

      return new Observable(observer => {
        captcha.showCaptcha(token => {
          captchaInProgress = false;

          operation.setContext(({ headers = {} }) => ({
            headers: {
              ...headers,
              "x-aws-waf-token": token
            }
          }));

          const subscriber = {
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer)
          };

          // Retry last failed request
          forward(operation).subscribe(subscriber);
        });
      });
    }
  });

  if (initError) {
    return <ErrorCard errors={[initError]} />;
  }

  if (!httpLink) {
    return <LoadingIndicator />;
  }

  const apolloClient = new ApolloClient({
    link: tenantLink
      .concat(authLink)
      .concat(sentryBreadcrumbLink)
      .concat(sentryErrorLink)
      .concat(wafChallengeLink)
      .concat(httpLink),
    cache,
    defaultOptions: {
      // Preserve graphql error data for useQuery for discerning between
      // authenication, authorization and server errors.
      watchQuery: {
        errorPolicy: "all"
      }
    }
  });

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
}
